import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowRightLeft, GraduationCap, MapPin, Calendar, Plus, 
  Mail, Phone, Star, CheckCircle, Clock, XCircle, Building2,
  Target, ChevronRight, Users, Trophy, AlertTriangle, Trash2,
  MessageSquare, Eye, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const INTEREST_LEVELS = [
  { id: 'high', label: 'High Interest', color: 'bg-green-500/20 text-green-400' },
  { id: 'medium', label: 'Medium Interest', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 'low', label: 'Exploring', color: 'bg-blue-500/20 text-blue-400' },
];

const CONTACT_TYPES = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'call', label: 'Phone Call', icon: Phone },
  { id: 'visit', label: 'Campus Visit', icon: MapPin },
  { id: 'offer', label: 'Offer Received', icon: Trophy },
];

const TransferPortalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalStatus, setPortalStatus] = useState({
    inPortal: false,
    entryDate: null,
    eligibilityYears: 4
  });
  const [schools, setSchools] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newSchool, setNewSchool] = useState({
    name: '',
    conference: '',
    division: 'D1',
    interestLevel: 'medium',
    hasOffer: false,
    notes: ''
  });
  const [newContact, setNewContact] = useState({
    schoolId: '',
    type: 'email',
    date: '',
    coachName: '',
    notes: ''
  });

  useEffect(() => {
    fetchPortalData();
  }, []);

  const fetchPortalData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/transfer-portal/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const statusData = response.data.status || {};
      setPortalStatus({
        inPortal: statusData.inPortal || statusData.in_portal || false,
        entryDate: statusData.entryDate || statusData.entry_date || null,
        eligibilityYears: statusData.eligibilityYears || 4
      });
      
      // Map backend field names to frontend
      const fetchedSchools = (response.data.schools || []).map(s => ({
        id: s.id,
        name: s.name,
        conference: s.conference,
        division: s.division,
        interestLevel: s.interestLevel,
        hasOffer: s.hasOffer,
        notes: s.notes
      }));
      setSchools(fetchedSchools);
      
      const fetchedContacts = (response.data.contacts || []).map(c => ({
        id: c.id,
        schoolId: c.schoolId,
        type: c.type,
        date: c.date,
        coachName: c.coachName,
        notes: c.notes
      }));
      setContacts(fetchedContacts);
    } catch (error) {
      console.error('Failed to fetch portal data:', error);
      setSchools([]);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePortalStatus = async () => {
    const newStatus = !portalStatus.inPortal;
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/transfer-portal/status`, {
        inPortal: newStatus,
        entryDate: newStatus ? new Date().toISOString() : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Update locally
    }
    setPortalStatus({
      ...portalStatus,
      inPortal: newStatus,
      entryDate: newStatus ? new Date().toISOString() : null
    });
    toast.success(newStatus ? 'Entered transfer portal' : 'Withdrew from portal');
  };

  const handleAddSchool = async () => {
    if (!newSchool.name) {
      toast.error('Please enter school name');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      const schoolData = {
        name: newSchool.name,
        conference: newSchool.conference,
        division: newSchool.division,
        interestLevel: newSchool.interestLevel,
        hasOffer: newSchool.hasOffer,
        notes: newSchool.notes
      };
      
      await axios.post(`${API}/transfer-portal/schools`, schoolData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('School added to your list');
      setShowAddSchool(false);
      setNewSchool({
        name: '',
        conference: '',
        division: 'D1',
        interestLevel: 'medium',
        hasOffer: false,
        notes: ''
      });
      fetchPortalData();
    } catch (error) {
      console.error('Failed to add school:', error);
      toast.error('Failed to add school');
    }
  };

  const handleAddContact = async () => {
    if (!newContact.schoolId || !newContact.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('aura_token');
      const contactData = {
        schoolId: newContact.schoolId,
        type: newContact.type,
        date: newContact.date,
        coachName: newContact.coachName,
        notes: newContact.notes
      };
      
      await axios.post(`${API}/transfer-portal/contacts`, contactData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contact logged');
      setShowAddContact(false);
      setNewContact({
        schoolId: '',
        type: 'email',
        date: '',
        coachName: '',
        notes: ''
      });
      fetchPortalData();
    } catch (error) {
      console.error('Failed to log contact:', error);
      toast.error('Failed to log contact');
    }
  };

  const removeSchool = async (id) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/transfer-portal/schools/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('School removed');
      fetchPortalData();
    } catch (error) {
      console.error('Failed to remove school:', error);
      toast.error('Failed to remove school');
    }
  };

  const getSchoolById = (id) => schools.find(s => s.id === id);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const offersCount = schools.filter(s => s.hasOffer).length;
  const highInterestCount = schools.filter(s => s.interestLevel === 'high').length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Transfer Portal
            </h1>
            <p className="text-aura-text-muted mt-1">Manage your transfer process and track interested schools</p>
          </div>
          <Badge className={`${portalStatus.inPortal ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'} border-0 text-sm px-4 py-2`}>
            {portalStatus.inPortal ? 'In Portal' : 'Not in Portal'}
          </Badge>
        </div>

        {/* Portal Status Card */}
        <Card className={`border-2 ${portalStatus.inPortal ? 'bg-blue-500/10 border-blue-500/30' : 'bg-aura-surface border-white/10'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${portalStatus.inPortal ? 'bg-blue-500' : 'bg-white/10'}`}>
                  <ArrowRightLeft className={`w-7 h-7 ${portalStatus.inPortal ? 'text-white' : 'text-aura-text-muted'}`} />
                </div>
                <div>
                  <p className="font-heading font-bold text-xl text-white uppercase">Portal Status</p>
                  {portalStatus.inPortal && portalStatus.entryDate && (
                    <p className="text-sm text-aura-text-muted">
                      Entered: {new Date(portalStatus.entryDate).toLocaleDateString()}
                    </p>
                  )}
                  {!portalStatus.inPortal && (
                    <p className="text-sm text-aura-text-muted">
                      Toggle to enter the transfer portal
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-aura-text-muted">
                  {portalStatus.inPortal ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={portalStatus.inPortal}
                  onCheckedChange={togglePortalStatus}
                  data-testid="portal-toggle"
                />
              </div>
            </div>

            {portalStatus.inPortal && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-400 font-medium">Important Reminder</p>
                    <p className="text-xs text-aura-text-muted mt-1">
                      Contact your compliance office before making any official decisions. 
                      You have 30 days to withdraw from the portal without penalty.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <GraduationCap className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{schools.length}</p>
              <p className="text-xs text-aura-text-muted">Schools Tracked</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{offersCount}</p>
              <p className="text-xs text-aura-text-muted">Offers</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{highInterestCount}</p>
              <p className="text-xs text-aura-text-muted">High Interest</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{contacts.length}</p>
              <p className="text-xs text-aura-text-muted">Contacts</p>
            </CardContent>
          </Card>
        </div>

        {/* Readiness Link */}
        <Card className="bg-gradient-to-r from-blue-500/20 to-purple-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="font-heading font-bold text-white uppercase">Boost Your Transfer Value</p>
                  <p className="text-sm text-aura-text-muted">Higher Readiness Score = More Interest from Schools</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/readiness')}
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              >
                View Readiness <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="schools">
          <TabsList className="w-full bg-aura-surface border border-white/10">
            <TabsTrigger value="schools" className="flex-1 data-[state=active]:bg-aura-primary">
              Schools ({schools.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex-1 data-[state=active]:bg-aura-primary">
              Contact Log ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Schools Tab */}
          <TabsContent value="schools" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Dialog open={showAddSchool} onOpenChange={setShowAddSchool}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600" data-testid="add-school-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add School
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading font-bold text-xl uppercase">Add School</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>School Name *</Label>
                      <Input
                        value={newSchool.name}
                        onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                        placeholder="e.g., University of Michigan"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                        data-testid="school-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Conference</Label>
                        <Input
                          value={newSchool.conference}
                          onChange={(e) => setNewSchool({...newSchool, conference: e.target.value})}
                          placeholder="e.g., Big Ten"
                          className="bg-aura-bg border-white/10 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label>Division</Label>
                        <Select value={newSchool.division} onValueChange={(v) => setNewSchool({...newSchool, division: v})}>
                          <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-aura-surface border-white/10">
                            {['D1', 'D2', 'D3', 'NAIA', 'JUCO'].map(d => (
                              <SelectItem key={d} value={d} className="text-white">{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Interest Level</Label>
                      <Select value={newSchool.interestLevel} onValueChange={(v) => setNewSchool({...newSchool, interestLevel: v})}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {INTEREST_LEVELS.map(level => (
                            <SelectItem key={level.id} value={level.id} className="text-white">{level.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-sm text-white">Has Offer?</span>
                      <Switch
                        checked={newSchool.hasOffer}
                        onCheckedChange={(checked) => setNewSchool({...newSchool, hasOffer: checked})}
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input
                        value={newSchool.notes}
                        onChange={(e) => setNewSchool({...newSchool, notes: e.target.value})}
                        placeholder="Any notes about this school"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <Button onClick={handleAddSchool} className="w-full bg-blue-500 hover:bg-blue-600">
                      Add School
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {schools.length === 0 ? (
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="py-12 text-center">
                  <GraduationCap className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                  <p className="text-aura-text-muted">No schools added yet</p>
                  <p className="text-sm text-aura-text-subtle mt-1">
                    Start tracking schools you're interested in
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {schools.map(school => {
                  const interest = INTEREST_LEVELS.find(l => l.id === school.interestLevel);
                  return (
                    <Card key={school.id} className="bg-aura-surface border-white/10" data-testid={`school-${school.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6 text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-heading font-bold text-white">{school.name}</p>
                              {school.hasOffer && (
                                <Badge className="bg-green-500/20 text-green-400 border-0">
                                  <Trophy className="w-3 h-3 mr-1" />
                                  Offer
                                </Badge>
                              )}
                              <Badge className={`${interest?.color} border-0`}>
                                {interest?.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-aura-text-muted mt-1">
                              {school.conference && `${school.conference} • `}{school.division}
                            </p>
                            {school.notes && (
                              <p className="text-sm text-aura-text-subtle mt-2 italic">"{school.notes}"</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSchool(school.id)}
                            className="text-aura-text-muted hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-500 hover:bg-purple-600" data-testid="add-contact-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Log Contact
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading font-bold text-xl uppercase">Log Contact</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>School *</Label>
                      <Select value={newContact.schoolId} onValueChange={(v) => setNewContact({...newContact, schoolId: v})}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                          <SelectValue placeholder="Select school" />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {schools.map(school => (
                            <SelectItem key={school.id} value={school.id} className="text-white">{school.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type</Label>
                        <Select value={newContact.type} onValueChange={(v) => setNewContact({...newContact, type: v})}>
                          <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-aura-surface border-white/10">
                            {CONTACT_TYPES.map(type => (
                              <SelectItem key={type.id} value={type.id} className="text-white">{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          value={newContact.date}
                          onChange={(e) => setNewContact({...newContact, date: e.target.value})}
                          className="bg-aura-bg border-white/10 text-white mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Coach Name</Label>
                      <Input
                        value={newContact.coachName}
                        onChange={(e) => setNewContact({...newContact, coachName: e.target.value})}
                        placeholder="e.g., Coach Johnson"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input
                        value={newContact.notes}
                        onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                        placeholder="Summary of the conversation"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <Button onClick={handleAddContact} className="w-full bg-purple-500 hover:bg-purple-600">
                      Log Contact
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {contacts.length === 0 ? (
              <Card className="bg-aura-surface border-white/10">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                  <p className="text-aura-text-muted">No contacts logged yet</p>
                  <p className="text-sm text-aura-text-subtle mt-1">
                    Track all communication with coaches and schools
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {contacts.sort((a, b) => new Date(b.date) - new Date(a.date)).map(contact => {
                  const school = getSchoolById(contact.schoolId);
                  const contactType = CONTACT_TYPES.find(t => t.id === contact.type);
                  const Icon = contactType?.icon || Mail;
                  return (
                    <Card key={contact.id} className="bg-aura-surface border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-purple-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-white">{school?.name || 'Unknown School'}</p>
                              <span className="text-xs text-aura-text-muted">
                                {new Date(contact.date).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-aura-text-muted">
                              {contactType?.label}
                              {contact.coachName && ` with ${contact.coachName}`}
                            </p>
                            {contact.notes && (
                              <p className="text-sm text-aura-text-subtle mt-2">{contact.notes}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default TransferPortalPage;
